import React, { useState, useEffect, useRef } from 'react';
import { 
  FaRocket, 
  FaMoneyBillWave, 
  FaCheck,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import axios from 'axios';
import Footer from '../components/Footer';

// Google reCAPTCHA import
import ReCAPTCHA from "react-google-recaptcha";

function NiveshManthan() {
  const [showRoleSelector, setShowRoleSelector] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [showStartupForm, setShowStartupForm] = useState(false);
  const [showInvestorForm, setShowInvestorForm] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // reCAPTCHA states
  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  
  // API data states
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state - Common for both startup and investor
  const [formData, setFormData] = useState({
    // Common fields
    applicationType: '', // 'startup' or 'investor'
    fullName: '',
    organizationName: '', // For investor: organization/fund name, For startup: startup name
    email: '',
    mobileNumber: '',
    cityState: '',
    
    // Startup specific fields
    founderName: '',
    startupName: '',
    businessStage: '',
    industrySector: '',
    yearOfIncorporation: '',
    legalEntityType: '',
    fundingSought: '',
    useOfFunds: [],
    businessSummary: '',
    revenueLastFY: '',
    
    // Investor specific fields
    investorCategory: [],
    preferredSectors: [],
    ticketSize: '',
    geographyPreference: [],
    engagementInterests: [],
    
    // Common declaration fields
    agreeToTerms: false,
    agreeToContact: false,
    
    // File upload
    pitchDeck: null
  });

  // API URLs
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_PROGRAM = `${API_BASE_URL}/UI/nivesh-manthan`;
  const API_MEDIA = `${API_BASE_URL}/UImedia/upload`; // Fixed URL - removed typo
  const API_REGISTER = `${API_BASE_URL}/niveshmanthan-applications`; // Fixed URL
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Fetch API data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(API_PROGRAM);
        
        if (response.data.success) {
          setApiData(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch data');
        }
      } catch (err) {
        console.error('Error fetching Nivesh Manthan data:', err);
        setError(err.message || 'Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_PROGRAM]);

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setShowRoleSelector(false);
    
    if (role === 'startup') {
      setShowStartupForm(true);
      setShowInvestorForm(false);
      // Reset form with startup type
      setFormData({
        ...formData,
        applicationType: 'startup',
        fullName: '',
        organizationName: '',
        email: '',
        mobileNumber: '',
        cityState: '',
        founderName: '',
        startupName: '',
        businessStage: '',
        industrySector: '',
        yearOfIncorporation: '',
        legalEntityType: '',
        fundingSought: '',
        useOfFunds: [],
        businessSummary: '',
        revenueLastFY: '',
        agreeToTerms: false,
        agreeToContact: false,
        pitchDeck: null
      });
    } else {
      setShowStartupForm(false);
      setShowInvestorForm(true);
      // Reset form with investor type
      setFormData({
        ...formData,
        applicationType: 'investor',
        fullName: '',
        organizationName: '',
        email: '',
        mobileNumber: '',
        cityState: '',
        investorCategory: [],
        preferredSectors: [],
        ticketSize: '',
        geographyPreference: [],
        engagementInterests: [],
        agreeToTerms: false,
        agreeToContact: false,
        pitchDeck: null
      });
    }
    
    // Reset reCAPTCHA when switching forms
    resetRecaptcha();
  };

  // Handle back to role selector
  const handleBackToRoles = () => {
    setShowRoleSelector(true);
    setShowStartupForm(false);
    setShowInvestorForm(false);
    setSelectedRole('');
    resetRecaptcha();
  };

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Handle reCAPTCHA expire
  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  // Handle reCAPTCHA error
  const handleRecaptchaError = () => {
    setRecaptchaToken(null);
  };

  // Reset reCAPTCHA
  const resetRecaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setRecaptchaToken(null);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'useOfFunds' || name === 'investorCategory' || 
          name === 'preferredSectors' || name === 'geographyPreference' || 
          name === 'engagementInterests') {
        const updatedArray = formData[name]?.includes(value)
          ? formData[name].filter(item => item !== value)
          : [...(formData[name] || []), value];
        setFormData({ ...formData, [name]: updatedArray });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // File validation
    const allowedTypes = ['application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      setFileError('Only PDF files are allowed');
      return;
    }
    
    if (file.size > maxSize) {
      setFileError('File size should be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    setFileError('');
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Upload file to media endpoint and get media ID
  const uploadFileToServer = async (file) => {
    if (!file) return null;

    try {
      setUploadingFile(true);
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('name', file.name);
      formDataToSend.append('type', file.type);

      const response = await fetch(API_MEDIA, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      
      // Check different response formats
      let mediaId;
      if (result?.data?.id) {
        mediaId = result.data.id;
      } else if (result?.id) {
        mediaId = result.id;
      } else if (result?._id) {
        mediaId = result._id;
      } else if (result?.data?._id) {
        mediaId = result.data._id;
      }
      
      if (!mediaId) {
        console.error("Media uploaded but ID missing:", result);
        return null;
      }

      return mediaId;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle form submission - SINGLE ENDPOINT FOR BOTH
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Common validation
    if (!recaptchaToken) {
      alert('Please complete the CAPTCHA verification');
      return;
    }
    
    // Startup specific validation
    if (formData.applicationType === 'startup') {
      if (!formData.agreeToTerms) {
        alert('Please accept the terms and conditions to proceed');
        return;
      }
      
      if (!selectedFile) {
        setFileError('Please upload your pitch deck (PDF)');
        return;
      }
    }
    
    // Investor specific validation
    if (formData.applicationType === 'investor') {
      if (!formData.agreeToContact) {
        alert('Please accept the declaration to proceed');
        return;
      }
    }
    
    setIsSubmitting(true);
    setFileError('');

    try {
      // Step 1: Upload file to server (for startup only)
      let pitchDeckMediaId = null;
      if (selectedFile && formData.applicationType === 'startup') {
        pitchDeckMediaId = await uploadFileToServer(selectedFile);
        if (!pitchDeckMediaId) {
          // File upload failed but it's required for startup
          alert('File upload failed. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // Step 2: Prepare application data
      const applicationData = {
        applicationType: formData.applicationType,
        
        // Common fields
        fullName: formData.fullName,
        email: formData.email,
        mobileNumber: formData.mobileNumber,
        
        // Startup specific
        startupName: formData.startupName || formData.organizationName,
        founderName: formData.founderName || formData.fullName,
        businessStage: formData.businessStage || '',
        industrySector: formData.industrySector || '',
        yearOfIncorporation: formData.yearOfIncorporation || '',
        legalEntityType: formData.legalEntityType || '',
        fundingSought: formData.fundingSought || '',
        useOfFunds: formData.useOfFunds || [],
        businessSummary: formData.businessSummary || '',
        revenueLastFY: formData.revenueLastFY || '',
        agreeToTerms: formData.agreeToTerms || false,
        
        // Investor specific
        organizationName: formData.organizationName || '',
        investorCategory: formData.investorCategory || [],
        preferredSectors: formData.preferredSectors || [],
        ticketSize: formData.ticketSize || '',
        geographyPreference: formData.geographyPreference || [],
        engagementInterests: formData.engagementInterests || [],
        agreeToContact: formData.agreeToContact || false,
        
        // Optional fields
        cityState: formData.cityState || '',
        
        // File reference (for startup only)
        pitchDeck: pitchDeckMediaId,
        
        // reCAPTCHA token
        recaptchaToken: recaptchaToken
      };

      // Step 3: Submit to backend endpoint (SINGLE ENDPOINT)
      const response = await fetch(API_REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        let errorMessage = result.message || 'Failed to submit application';
        
        if (result.error) {
          errorMessage = result.error;
        }
        if (result.errors) {
          errorMessage = Array.isArray(result.errors) 
            ? result.errors.join(', ') 
            : JSON.stringify(result.errors);
        }
        
        throw new Error(errorMessage);
      }
      
      // Success
      if (response.ok && (result.success === true || result.success === undefined)) {
        // Set success message based on application type
        if (formData.applicationType === 'startup') {
          setSuccessMessage('Thank you. Our screening team will review your application and reach out if shortlisted.');
        } else {
          setSuccessMessage('Thank you for registering as an investor/partner. Our team will contact you shortly for onboarding and next steps.');
        }
        
        setShowSuccessPopup(true);
        
        // Reset form
        resetForm();
      } else {
        throw new Error(result.message || 'Application submission failed');
      }
      
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      
      // Reset reCAPTCHA on error
      resetRecaptcha();
      
      let userMessage = error.message;
      
      if (error.message.includes('reCAPTCHA')) {
        userMessage = 'reCAPTCHA verification failed. Please try again.';
      } else if (error.message.includes('validation')) {
        userMessage = 'Please check all required fields and try again.';
      } else if (error.message.includes('network')) {
        userMessage = 'Network error. Please check your connection.';
      }
      
      alert(`Error: ${userMessage}`);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      applicationType: '',
      fullName: '',
      organizationName: '',
      email: '',
      mobileNumber: '',
      cityState: '',
      founderName: '',
      startupName: '',
      businessStage: '',
      industrySector: '',
      yearOfIncorporation: '',
      legalEntityType: '',
      fundingSought: '',
      useOfFunds: [],
      businessSummary: '',
      revenueLastFY: '',
      investorCategory: [],
      preferredSectors: [],
      ticketSize: '',
      geographyPreference: [],
      engagementInterests: [],
      agreeToTerms: false,
      agreeToContact: false,
      pitchDeck: null
    });
    setSelectedFile(null);
    resetRecaptcha();
    
    // Reset file input field
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-amber-500 mb-4" />
        <p className="text-xl text-amber-300">Loading Nivesh Manthan...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
        <FaExclamationTriangle className="text-4xl text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-red-400 mb-2">Failed to Load Content</h2>
        <p className="text-amber-200 text-center max-w-md">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-6 bg-amber-500 text-gray-900 font-bold py-2 px-6 rounded-lg hover:bg-amber-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // No data state
  if (!apiData) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
        <FaExclamationTriangle className="text-4xl text-yellow-500 mb-4" />
        <h2 className="text-2xl font-bold text-yellow-400 mb-2">No Active Program Found</h2>
        <p className="text-amber-200 text-center max-w-md">
          There is currently no active Nivesh Manthan program. Please check back later.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-700 text-white">
      {/* Hero Section */}
      <header className="relative py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        <div className="relative max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            {apiData.heroSection.title}
          </h1>
          <p className="text-lg text-amber-300 mb-4">
            {apiData.heroSection.subtitle}
          </p>
          <p className="text-xl md:text-2xl text-amber-100 max-w-3xl mx-auto mb-12 italic">
            {apiData.heroSection.tagline}
          </p>
          
          {/* Primary CTA Buttons */}
          <div className="flex flex-col md:flex-row gap-4 justify-center mb-12">
            {apiData.heroSection.ctaButtons.map((button) => (
              <button
                key={button.id}
                onClick={() => handleRoleSelect(button.id)}
                className={`bg-gradient-to-r ${button.gradient} text-gray-900 font-bold py-4 px-8 rounded-full text-lg hover:bg-gradient-to-r hover:${button.hoverGradient} transition-all transform hover:scale-105`}
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Role Selector */}
        {showRoleSelector && (
          <section className="mb-16">
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/30">
              <h2 className="text-3xl font-bold mb-2 text-center text-amber-300">
                {apiData.roleSelector.title}
              </h2>
              <p className="text-center text-amber-200 mb-8">
                {apiData.roleSelector.subtitle}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {apiData.roleSelector.roles.map((role) => {
                  const colorClasses = {
                    green: {
                      border: 'border-green-500/30 hover:border-green-500/60',
                      text: 'text-green-400',
                      check: 'text-green-400',
                      bg: 'from-green-500 to-emerald-500'
                    },
                    blue: {
                      border: 'border-blue-500/30 hover:border-blue-500/60',
                      text: 'text-blue-400',
                      check: 'text-blue-400',
                      bg: 'from-blue-500 to-cyan-500'
                    }
                  };

                  return (
                    <div 
                      key={role.id}
                      onClick={() => handleRoleSelect(role.id)}
                      className={`bg-gradient-to-br from-gray-800 to-gray-900/50 p-8 rounded-2xl ${colorClasses[role.color].border} transition-all cursor-pointer text-center hover:scale-105`}
                    >
                      <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r ${colorClasses[role.color].bg} text-gray-900 font-bold text-2xl rounded-full mb-6 mx-auto`}>
                        {role.id === 'startup' ? '🚀' : '💰'}
                      </div>
                      <h3 className={`text-2xl font-bold ${colorClasses[role.color].text} mb-4`}>
                        {role.title}
                      </h3>
                      <p className="text-amber-100 mb-4">{role.description}</p>
                      <ul className="text-left text-amber-200 text-sm space-y-2">
                        {role.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <FaCheck className={`${colorClasses[role.color].check}`} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* What is Nivesh Manthan? */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/70 backdrop-blur-sm rounded-xl p-8 max-w-4xl mx-auto border border-amber-500/20">
            <h2 className="text-2xl font-bold mb-4 text-amber-300">
              {apiData.whatIsNiveshManthan.title}
            </h2>
            <p className="text-lg text-white mb-4">
              {apiData.whatIsNiveshManthan.description}
            </p>
            <p className="text-lg text-amber-100 font-bold">
              {apiData.whatIsNiveshManthan.tagline}
            </p>
          </div>
        </section>

        {/* Why Nivesh Manthan? */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
            {apiData.whyNiveshManthan.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {apiData.whyNiveshManthan.reasons.map((reason, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-6 rounded-xl border border-amber-500/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-amber-400 text-3xl font-bold">
                    {reason.number}
                  </div>
                  <h3 className="text-xl font-bold text-amber-200">{reason.title}</h3>
                </div>
                <p className="text-amber-100">{reason.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
            {apiData.howItWorks.title}
          </h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-gradient-to-b from-amber-500 to-yellow-500"></div>
            
            <div className="space-y-12">
              {apiData.howItWorks.steps.map((item, index) => (
                <div key={index} className={`relative ${index % 2 === 0 ? 'md:pr-1/2 md:pl-12' : 'md:pl-1/2 md:pr-12'}`}>
                  <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-amber-500 rounded-full border-4 border-gray-900"></div>
                  
                  <div className="ml-12 md:ml-0 bg-gradient-to-br from-gray-800 to-gray-900/50 p-6 rounded-xl border border-amber-500/20">
                    <div className="text-amber-400 font-bold mb-2">{item.step}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-amber-100">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Should Participate? */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/20">
            <h2 className="text-3xl font-bold mb-6 text-amber-300">
              {apiData.whoShouldParticipate.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {apiData.whoShouldParticipate.categories.map((category, index) => {
                const colorClasses = {
                  green: { text: 'text-green-400', check: 'text-green-400' },
                  blue: { text: 'text-blue-400', check: 'text-blue-400' }
                };

                return (
                  <div key={index}>
                    <h3 className={`text-2xl font-bold mb-4 ${colorClasses[category.color].text}`}>
                      {category.title}
                    </h3>
                    <ul className="space-y-3">
                      {category.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="min-w-2 h-2 bg-amber-400 rounded-full mt-2"></div>
                          <span className="text-amber-100">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Program Format */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
            {apiData.programFormat.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {apiData.programFormat.formats.map((format, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-6 rounded-xl border border-amber-500/20 text-center">
                <div className="text-amber-400 text-3xl font-bold mb-4">{format.number}</div>
                <h3 className="text-xl font-bold text-amber-200 mb-2">{format.title}</h3>
                <p className="text-amber-100">{format.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Fee & Commercial Structure */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/20">
            <h2 className="text-3xl font-bold mb-6 text-amber-300">
              {apiData.feeStructure.title}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {apiData.feeStructure.sections.map((section, index) => {
                const colorClasses = {
                  green: { 
                    text: 'text-green-400',
                    bg: 'bg-green-500/10',
                    border: 'border-green-500/30'
                  },
                  blue: { 
                    text: 'text-blue-400',
                    bg: 'bg-blue-500/10',
                    border: 'border-blue-500/30'
                  }
                };

                return (
                  <div key={index} className={`p-6 ${colorClasses[section.color].bg} rounded-xl ${colorClasses[section.color].border}`}>
                    <h3 className={`text-2xl font-bold mb-4 ${colorClasses[section.color].text}`}>
                      {section.title}
                    </h3>
                    <ul className="space-y-4">
                      {section.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="min-w-2 h-2 bg-amber-400 rounded-full mt-2"></div>
                          <div>
                            <p className="text-amber-100 font-bold">{point.title}</p>
                            {point.description && (
                              <p className="text-amber-200/70 text-sm mt-1">{point.description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mb-16 text-center">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-12 border border-amber-500/30">
            <h2 className="text-4xl font-bold mb-6 text-white">
              {apiData.finalCta.title}
            </h2>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center mt-8">
              {apiData.finalCta.ctaButtons.map((button) => (
                <button
                  key={button.id}
                  onClick={() => handleRoleSelect(button.id)}
                  className={`bg-gradient-to-r ${button.gradient} text-gray-900 font-bold py-4 px-8 rounded-full text-lg hover:bg-gradient-to-r hover:${button.hoverGradient} transition-all transform hover:scale-105`}
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Registration Forms */}
        {/* Startup Form */}
        {showStartupForm && (
          <section id="startup-form" className="mb-20">
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-green-300">
                  🟦 Startup / MSME Application (Funding Seeker)
                </h2>
                <button
                  onClick={handleBackToRoles}
                  className="text-amber-300 hover:text-amber-400 text-sm font-medium"
                >
                  ← Back to Role Selection
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Section A: Basic Details */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-green-500/30 text-green-400">
                    Section A: Basic Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Startup / MSME Name (Text – Required) *
                      </label>
                      <input
                        type="text"
                        name="startupName"
                        value={formData.startupName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter your company name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Founder Name (Text – Required) *
                      </label>
                      <input
                        type="text"
                        name="founderName"
                        value={formData.founderName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter founder name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Email (Email – Required) *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter email address"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Mobile Number (Phone – Required) *
                      </label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        required
                        pattern="[0-9]{10}"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter 10-digit mobile number"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-amber-300 mb-2 font-medium">
                        City & State (Text)
                      </label>
                      <input
                        type="text"
                        name="cityState"
                        value={formData.cityState}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter city and state"
                      />
                    </div>
                  </div>
                </div>

                {/* Section B: Business Profile */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-green-500/30 text-green-400">
                    Section B: Business Profile
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Business Stage (Dropdown – Required) *
                      </label>
                      <select
                        name="businessStage"
                        value={formData.businessStage}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                      >
                        <option value="">Select Business Stage</option>
                        {apiData.businessStages.map((stage, index) => (
                          <option key={index} value={stage} className="bg-gray-800">
                            {stage}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Industry / Sector (Text)
                      </label>
                      <input
                        type="text"
                        name="industrySector"
                        value={formData.industrySector}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter industry/sector"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Year of Incorporation (Year)
                      </label>
                      <input
                        type="number"
                        name="yearOfIncorporation"
                        value={formData.yearOfIncorporation}
                        onChange={handleInputChange}
                        min="1900"
                        max="2024"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="YYYY"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Legal Entity Type (Dropdown)
                      </label>
                      <select
                        name="legalEntityType"
                        value={formData.legalEntityType}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                      >
                        <option value="">Select Entity Type</option>
                        {apiData.entityTypes.map((entity, index) => (
                          <option key={index} value={entity} className="bg-gray-800">
                            {entity}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section C: Funding Details */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-green-500/30 text-green-400">
                    Section C: Funding Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Funding Sought (Dropdown) *
                      </label>
                      <select
                        name="fundingSought"
                        value={formData.fundingSought}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                      >
                        <option value="">Select Funding Range</option>
                        {apiData.fundingRanges.map((range, index) => (
                          <option key={index} value={range} className="bg-gray-800">
                            {range}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-amber-300 mb-2 font-medium">
                        Use of Funds (Multi-select)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {apiData.fundUsageOptions.map((option, index) => (
                          <label
                            key={index}
                            className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.useOfFunds.includes(option) ? 'bg-green-500/20 border border-green-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                          >
                            <input
                              type="checkbox"
                              name="useOfFunds"
                              value={option}
                              checked={formData.useOfFunds.includes(option)}
                              onChange={handleInputChange}
                              className="text-green-500 focus:ring-green-500"
                            />
                            <span className="text-amber-100">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section D: Pitch Readiness */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-green-500/30 text-green-400">
                    Section D: Pitch Readiness
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Pitch Deck Upload (PDF – Required) *
                      </label>
                      <div className="mt-2">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <FaUpload className="w-8 h-8 mb-4 text-gray-400" />
                            <p className="mb-2 text-sm text-gray-400">
                              <span className="font-semibold">Click to upload Pitch Deck</span>
                            </p>
                            <p className="text-xs text-gray-500">PDF only (Max 10MB)</p>
                          </div>
                          <input
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".pdf"
                            required
                          />
                        </label>
                        
                        {/* Show selected file info */}
                        {selectedFile && (
                          <div className="mt-2 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-green-400 text-sm font-medium">
                                  {selectedFile.name}
                                </p>
                                <p className="text-green-400/70 text-xs">
                                  {formatFileSize(selectedFile.size)}
                                </p>
                              </div>
                              {uploadingFile && (
                                <FaSpinner className="animate-spin text-green-400" />
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Show file error */}
                        {fileError && (
                          <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                            <p className="text-red-400 text-sm">{fileError}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        One-line Business Summary (Textarea – Required, 300 chars max) *
                      </label>
                      <textarea
                        name="businessSummary"
                        value={formData.businessSummary}
                        onChange={handleInputChange}
                        rows="2"
                        maxLength="300"
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition resize-none"
                        placeholder="Describe your business in one line..."
                      />
                      <div className="text-right text-sm text-amber-400/70 mt-1">
                        {formData.businessSummary.length}/300 characters
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Revenue Last FY (Optional)
                      </label>
                      <input
                        type="text"
                        name="revenueLastFY"
                        value={formData.revenueLastFY}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        placeholder="Enter revenue amount"
                      />
                    </div>
                  </div>
                </div>

                {/* Section E: Declaration */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-green-500/30 text-green-400">
                    Section E: Declaration
                  </h3>
                  
                  <div className="flex items-start gap-3 p-6 bg-gray-700/30 rounded-lg border border-gray-600">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      required
                      className="mt-1 text-green-500 focus:ring-green-500"
                    />
                    <div>
                      <span className="block text-amber-300 mb-2 font-medium cursor-pointer">
                        ☑ I understand that:
                      </span>
                      <ul className="space-y-2 text-amber-100">
                        <li>• My application will go through screening</li>
                        <li>• ₹15,000 fee is applicable only if selected</li>
                        <li>• MASA's success-linked stake applies only on successful fundraise</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* reCAPTCHA Section */}
                <div className="pt-4">
                  <div className="mb-4">
                    <label className="block text-amber-300 mb-3 font-medium">
                      Security Verification *
                    </label>
                    
                    {/* Google reCAPTCHA */}
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpired}
                      onErrored={handleRecaptchaError}
                      theme="dark"
                      className="flex justify-center"
                    />
                    
                    {/* Privacy note */}
                    <p className="mt-3 text-xs text-amber-400/70">
                      This reCAPTCHA is required to prevent spam submissions. 
                      Your data is protected by Google's privacy policy.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting || uploadingFile || !formData.agreeToTerms || !recaptchaToken}
                    className={`w-full bg-gradient-to-r from-green-500 to-emerald-500 text-gray-900 font-bold py-4 px-6 rounded-lg transition-all transform focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || uploadingFile || !formData.agreeToTerms || !recaptchaToken ? 'opacity-50 cursor-not-allowed' : 'hover:from-green-600 hover:to-emerald-600 hover:scale-105'}`}
                  >
                    {isSubmitting || uploadingFile ? (
                      <span className="flex items-center justify-center gap-2">
                        <FaSpinner className="animate-spin" />
                        {uploadingFile ? 'UPLOADING FILE...' : 'SUBMITTING FOR SCREENING...'}
                      </span>
                    ) : (
                      '🔘 Submit for Screening'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {/* Investor Form */}
        {showInvestorForm && (
          <section id="investor-form" className="mb-20">
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/30">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-blue-300">
                  🟩 Investor / Partner Registration
                </h2>
                <button
                  onClick={handleBackToRoles}
                  className="text-amber-300 hover:text-amber-400 text-sm font-medium"
                >
                  ← Back to Role Selection
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Section A: Profile Details */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-blue-500/30 text-blue-400">
                    Section A: Profile Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Full Name (Text – Required) *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Organization / Fund Name (Text – Required) *
                      </label>
                      <input
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                        placeholder="Enter organization/fund name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Email (Email – Required) *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                        placeholder="Enter email address"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Mobile Number (Phone – Required) *
                      </label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        required
                        pattern="[0-9]{10}"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                        placeholder="Enter 10-digit mobile number"
                      />
                    </div>
                  </div>
                </div>

                {/* Section B: Investor Type */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-blue-500/30 text-blue-400">
                    Section B: Investor Type
                  </h3>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                      Investor Category (Multi-select – Required) *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {apiData.investorCategories.map((category, index) => (
                        <label
                          key={index}
                          className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.investorCategory.includes(category) ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                        >
                          <input
                            type="checkbox"
                            name="investorCategory"
                            value={category}
                            checked={formData.investorCategory.includes(category)}
                            onChange={handleInputChange}
                            
                            className="text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-amber-100">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section C: Investment Preferences */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-blue-500/30 text-blue-400">
                    Section C: Investment Preferences
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-amber-300 mb-2 font-medium">
                        Preferred Sectors (Multi-select)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {apiData.sectorOptions.map((sector, index) => (
                          <label
                            key={index}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${formData.preferredSectors.includes(sector) ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                          >
                            <input
                              type="checkbox"
                              name="preferredSectors"
                              value={sector}
                              checked={formData.preferredSectors.includes(sector)}
                              onChange={handleInputChange}
                              className="text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-amber-100 text-sm">{sector}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                          Typical Ticket Size (Dropdown)
                        </label>
                        <select
                          name="ticketSize"
                          value={formData.ticketSize}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-white outline-none transition"
                        >
                          <option value="">Select Ticket Size</option>
                          {apiData.ticketSizes.map((size, index) => (
                            <option key={index} value={size} className="bg-gray-800">
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                          Geography Preference (Multi-select)
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {apiData.geographyOptions.map((geo, index) => (
                            <label
                              key={index}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${formData.geographyPreference.includes(geo) ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                            >
                              <input
                                type="checkbox"
                                name="geographyPreference"
                                value={geo}
                                checked={formData.geographyPreference.includes(geo)}
                                onChange={handleInputChange}
                                className="text-blue-500 focus:ring-blue-500"
                              />
                              <span className="text-amber-100 text-sm">{geo}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section D: Engagement Interest */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-blue-500/30 text-blue-400">
                    Section D: Engagement Interest
                  </h3>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                      Interested in: (Checkboxes)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {apiData.engagementOptions.map((interest, index) => (
                        <label
                          key={index}
                          className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.engagementInterests.includes(interest) ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                        >
                          <input
                            type="checkbox"
                            name="engagementInterests"
                            value={interest}
                            checked={formData.engagementInterests.includes(interest)}
                            onChange={handleInputChange}
                            className="text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-amber-100">{interest}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <div>
                  <h3 className="text-xl font-bold mb-6 pb-2 border-b border-blue-500/30 text-blue-400">
                    Declaration
                  </h3>
                  
                  <div className="flex items-start gap-3 p-6 bg-gray-700/30 rounded-lg border border-gray-600">
                    <input
                      type="checkbox"
                      name="agreeToContact"
                      checked={formData.agreeToContact}
                      onChange={handleInputChange}
                      required
                      className="mt-1 text-blue-500 focus:ring-blue-500"
                    />
                    <div>
                      <span className="block text-amber-300 mb-2 font-medium cursor-pointer">
                        ☑ I agree to be contacted by the MASA Forum team for onboarding and next steps.
                      </span>
                    </div>
                  </div>
                </div>

                {/* reCAPTCHA Section */}
                <div className="pt-4">
                  <div className="mb-4">
                    <label className="block text-amber-300 mb-3 font-medium">
                      Security Verification *
                    </label>
                    
                    {/* Google reCAPTCHA */}
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpired}
                      onErrored={handleRecaptchaError}
                      theme="dark"
                      className="flex justify-center"
                    />
                    
                    {/* Privacy note */}
                    <p className="mt-3 text-xs text-amber-400/70">
                      This reCAPTCHA is required to prevent spam submissions. 
                      Your data is protected by Google's privacy policy.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.agreeToContact || !recaptchaToken}
                    className={`w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-gray-900 font-bold py-4 px-6 rounded-lg transition-all transform focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || !formData.agreeToContact || !recaptchaToken ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-cyan-600 hover:scale-105'}`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <FaSpinner className="animate-spin" />
                        SUBMITTING REGISTRATION...
                      </span>
                    ) : (
                      'Submit Registration'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}
      </main>

      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-amber-500/30 rounded-2xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-2xl text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-amber-300 mb-4">
                Registration Submitted Successfully!
              </h3>
              
              <p className="text-amber-100 mb-6">
                {successMessage}
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all"
                >
                  Return to Program
                </button>
                
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    handleBackToRoles();
                  }}
                  className="bg-gray-700 text-amber-100 py-3 px-6 rounded-lg hover:bg-gray-600 transition-all border border-gray-600"
                >
                  Back to Role Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default NiveshManthan;